import React, { useEffect, useMemo, useState } from 'react';
import {
  Building2,
  Clock3,
  MapPin,
  Sofa,
  WashingMachine,
  X,
} from 'lucide-react';
import ModalShell from './ModalShell';
import { haptic } from '../utils/vibration';
import useToast from '../hooks/useToast';
import { CompactListSkeleton } from './Skeleton';
import useModalBackButton from '../hooks/useModalBackButton';
import * as activityApi from '../services/activityApi';
import { formatPriceCoefficientLabel } from '../utils/priceCoefficient';
import { formatTimezoneDate } from '../utils/timezone';

const FIELD_LABELS = {
  name: 'Название',
  description: 'Описание',
  is_active: 'Активность',
  order_types: 'Типы заказов',
  client_type: 'Тип клиентов',
  price_coefficient: 'Коэффициент цены',
  price_coefficient_b2c: 'Коэффициент цены (физ. лица)',
  price_coefficient_b2b: 'Коэффициент цены (юр. лица)',
  address_rules: 'Адреса',
};

const ORDER_TYPE_LABELS = {
  washing: 'Стирка',
  dry_cleaning: 'Химчистка',
};

const CLIENT_TYPE_LABELS = {
  all: 'Все клиенты',
  b2c: 'Физ. лица',
  b2b: 'Юр. лица',
};

const HIDDEN_KEYS = new Set(['text', 'created_by', 'updated_by']);

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const safeStringify = (value) => {
  if (value === null || value === undefined) return '';
  try {
    return JSON.stringify(value);
  } catch (error) {
    return String(value);
  }
};

const parseArrayValue = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }
  return [];
};

const normalizeOrderTypes = (value) => Array.from(
  new Set(
    parseArrayValue(value)
      .map((item) => String(item ?? '').trim())
      .filter(Boolean)
  )
).sort((a, b) => a.localeCompare(b, 'ru'));

const normalizeAddressRules = (value) => parseArrayValue(value)
  .filter((item) => isPlainObject(item))
  .map((item) => ({
    region: String(item.region || '').trim(),
    city: String(item.city || '').trim(),
    city_part: String(item.city_part || '').trim(),
  }))
  .filter((item) => item.region || item.city || item.city_part)
  .sort((a, b) => safeStringify(a).localeCompare(safeStringify(b), 'ru'));

const normalizeComparableValue = (fieldKey, value) => {
  if (fieldKey === 'order_types') return normalizeOrderTypes(value);
  if (fieldKey === 'address_rules') return normalizeAddressRules(value);
  if (fieldKey === 'is_active') return Boolean(value);
  if (fieldKey === 'price_coefficient' || fieldKey === 'price_coefficient_b2c' || fieldKey === 'price_coefficient_b2b') {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? Number(numericValue.toFixed(4)) : null;
  }
  if (typeof value === 'string') return value.trim();
  return value;
};

const hasMeaningfulDifference = (fieldKey, oldValue, newValue) => (
  safeStringify(normalizeComparableValue(fieldKey, oldValue)) !== safeStringify(normalizeComparableValue(fieldKey, newValue))
);

const getOrderTypeMeta = (orderType) => {
  if (orderType === 'washing') {
    return {
      label: ORDER_TYPE_LABELS[orderType],
      icon: WashingMachine,
      className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300',
    };
  }

  if (orderType === 'dry_cleaning') {
    return {
      label: ORDER_TYPE_LABELS[orderType],
      icon: Sofa,
      className: 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/20 dark:text-fuchsia-300',
    };
  }

  return {
    label: orderType,
    icon: Building2,
    className: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  };
};

const formatAddressRule = (rule) => [rule.region, rule.city, rule.city_part].filter(Boolean).join(', ');

const formatValue = (fieldKey, value) => {
  if (value === null || value === undefined || value === '') return '—';

  if (fieldKey === 'client_type') {
    return CLIENT_TYPE_LABELS[String(value).trim()] || String(value);
  }

  if (fieldKey === 'is_active') {
    return value ? 'Активно' : 'Неактивно';
  }

  if (fieldKey === 'price_coefficient' || fieldKey === 'price_coefficient_b2c' || fieldKey === 'price_coefficient_b2b') {
    return formatPriceCoefficientLabel(value, '—');
  }

  if (typeof value === 'boolean') return value ? 'Да' : 'Нет';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value.trim() || '—';
  return safeStringify(value);
};

const renderBadgeList = (items) => {
  if (!items.length) {
    return <span className="text-gray-400">—</span>;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <span
            key={item.key}
            className={`inline-flex max-w-full items-center gap-1 rounded-md px-2 py-1 text-xs ${item.className}`}
            title={item.label}
          >
            {Icon ? <Icon className="h-3.5 w-3.5 shrink-0" /> : null}
            <span className="truncate">{item.label}</span>
          </span>
        );
      })}
    </div>
  );
};

const renderFieldValue = (fieldKey, value) => {
  if (fieldKey === 'order_types') {
    return renderBadgeList(
      normalizeOrderTypes(value).map((orderType) => ({
        key: orderType,
        ...getOrderTypeMeta(orderType),
      }))
    );
  }

  if (fieldKey === 'address_rules') {
    return renderBadgeList(
      normalizeAddressRules(value).map((rule, index) => ({
        key: `${rule.region}-${rule.city}-${rule.city_part}-${index}`,
        label: formatAddressRule(rule),
        icon: MapPin,
        className: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200',
      }))
    );
  }

  if (fieldKey === 'client_type') {
    return (
      <span className="whitespace-pre-wrap break-words text-sm">
        {CLIENT_TYPE_LABELS[String(value).trim()] || String(value)}
      </span>
    );
  }

  return (
    <span className="whitespace-pre-wrap break-words break-all text-sm">
      {formatValue(fieldKey, value)}
    </span>
  );
};

const buildChangeRows = (oldValue, newValue) => {
  if (!isPlainObject(oldValue) && !isPlainObject(newValue)) return [];

  const before = isPlainObject(oldValue) ? oldValue : {};
  const after = isPlainObject(newValue) ? newValue : {};
  const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]));

  return keys
    .filter((key) => !HIDDEN_KEYS.has(key))
    .filter((key) => hasMeaningfulDifference(key, before[key], after[key]))
    .map((key) => ({
      key,
      label: FIELD_LABELS[key] || key,
      oldValue: before[key],
      newValue: after[key],
    }));
};

const summarizeRow = (row) => {
  if (row.key === 'order_types') return 'Типы заказов обновлены';
  if (row.key === 'address_rules') return 'Адреса обновлены';
  return `${row.label}: ${formatValue(row.key, row.newValue)}`;
};

const DepartmentActivityModal = ({ open, onClose, department }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { showToast } = useToast();

  useModalBackButton(open, onClose);

  useEffect(() => {
    if (!open || !department?.id) return undefined;

    let cancelled = false;

    const loadHistory = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await activityApi.getDepartmentChangeHistory(department.id, 50, 0);
        if (cancelled) return;
        setLogs(Array.isArray(data) ? data : []);
      } catch (err) {
        if (cancelled) return;
        console.error('Ошибка загрузки истории подразделения:', err);
        const msg = err.message || 'Не удалось загрузить историю подразделения';
        setError(msg);
        showToast(msg, 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadHistory();

    return () => {
      cancelled = true;
    };
  }, [open, department?.id]);

  const visibleLogs = useMemo(() => (
    logs.filter((log) => log.action !== 'updated' || buildChangeRows(log.old_value_data, log.new_value_data).length > 0)
  ), [logs]);

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      variant="sheet"
      expandable={true}
      panelClassName="max-h-[88vh] overflow-hidden rounded-t-xl sm:max-w-3xl sm:rounded-xl"
    >
      <div className="flex max-h-[88vh] flex-col">
        <div className="border-b border-gray-200 px-4 py-4 dark:border-gray-800">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-base font-semibold text-gray-900 dark:text-gray-100">История подразделения</div>
              <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {department?.name || `Подразделение #${department?.id}`}
              </div>
            </div>
            <button
              type="button"
              onClick={() => { haptic('close'); onClose(); }}
              className="rounded-lg bg-gray-100 p-2 text-gray-600 transition hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto px-4 py-4">
          {loading ? (
            <CompactListSkeleton count={4} />
          ) : error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-200">
              {error}
            </div>
          ) : visibleLogs.length === 0 ? (
            <div className="py-10 text-center">
              <Building2 className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600" />
              <div className="mt-3 text-sm font-medium text-gray-900 dark:text-white">Изменений пока нет</div>
              <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Для этого подразделения журнал еще пуст.
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {visibleLogs.map((log) => {
                const changeRows = buildChangeRows(log.old_value_data, log.new_value_data);
                const compactSummary = changeRows.slice(0, 3).map(summarizeRow).join(' • ');

                return (
                  <div
                    key={log.id}
                    className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-gray-900"
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                        <Building2 className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {log.user_name || `Пользователь #${log.user_id}`}
                          </span>
                          <span className="text-gray-500 dark:text-gray-400">{log.action_label}</span>
                        </div>

                        <div className="mt-1 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                          <Clock3 className="h-3.5 w-3.5" />
                          <span>{formatTimezoneDate(log.created_date, true)}</span>
                        </div>

                        <div className="mt-2 whitespace-pre-wrap break-words break-all text-sm text-gray-800 dark:text-gray-200">
                          {compactSummary || log.summary || 'Без описания'}
                        </div>

                        {changeRows.length > 0 ? (
                          <details className="mt-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-800 dark:bg-gray-800/40">
                            <summary className="cursor-pointer list-none text-xs font-medium text-gray-700 dark:text-gray-200">
                              Что изменили
                            </summary>
                            <div className="mt-2 space-y-2">
                              {changeRows.map((row) => (
                                <div key={row.key} className="rounded-lg bg-white px-3 py-2 text-xs dark:bg-gray-900">
                                  <div className="font-medium text-gray-500 dark:text-gray-400">{row.label}</div>
                                  <div className="mt-1 grid gap-1 sm:grid-cols-2">
                                    <div className="min-w-0 text-gray-600 dark:text-gray-300">
                                      <span className="mb-1 block text-gray-400">Было:</span>
                                      {renderFieldValue(row.key, row.oldValue)}
                                    </div>
                                    <div className="min-w-0 text-gray-900 dark:text-gray-100">
                                      <span className="mb-1 block text-gray-400">Стало:</span>
                                      {renderFieldValue(row.key, row.newValue)}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </details>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </ModalShell>
  );
};

export default DepartmentActivityModal;
